import IconButton from "@material-ui/core/IconButton";
import DisableBulkIcon from "@material-ui/icons/CheckBox";
import EnableBulkIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import { dataProvider } from "api";
import T from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

import DatagridEx from "../../components/_extensions/react-admin/ra-ui-materialui/src/list/datagrid/DatagridEx";
import { ExcludeColumnsModal } from "./ExcludeColumnsModal";
import { ColumnSelectionForm, EXCLUDE } from "./SelectionDialog";
import { userDataSessionColumnsStorage } from "./UserDataStorage";
import { useSaveUserDataOnSuccess } from "../../utils/hooks/useSaveUserDataOnSuccess";
import { useResetUserDataOnError } from "../../utils/hooks/useResetUserDataOnError";

// CustomizableDatagrid allows to show/hide columns dynamically
// the preferences are stored on server

const getColumnName = (fieldProps) => {
	const reorder_name = fieldProps?.reorder_name;
	if (reorder_name) return reorder_name;
	const source = fieldProps?.source;
	if (source) return source;
	const label = fieldProps?.label;
	if (label && typeof label === "string") return label;
	if (label && typeof label === "object") return label?.props?.label;
	return undefined;
};

const getColumnNames = (children) => {
	return React.Children.map(children, (field) => getColumnName(field?.props)).filter(
		(item) => item
	);
};

const getColumnLabels = (children) => {
	return React.Children.map(
		children,
		(field) =>
			field && {
				source: getColumnName(field?.props),
				// label or reorder_name if explicit; otherwise undefined so FieldTitle auto-generates from resource+source
				label: field?.props?.label || field?.props?.reorder_name || undefined,
			}
	).filter((fieldProps) => getColumnName(fieldProps));
};

const getFilteredExcludedColumns = (excludedColumns, children) => {
	const datagridColumns = getColumnNames(children);
	const newExcludedColumns = datagridColumns.reduce((accumulator, columnName) => {
		if (excludedColumns[columnName] === EXCLUDE) {
			accumulator[columnName] = EXCLUDE;
		}
		return accumulator;
	}, {});
	return newExcludedColumns;
};

const getDefaultHiddenColumns = (children) => {
	const defaults = {};
	React.Children.forEach(children, (field) => {
		if (field?.props?.defaultHidden) {
			const name = getColumnName(field.props);
			if (name) {
				defaults[name] = EXCLUDE;
			}
		}
	});
	return defaults;
};

const getExcludedColumns = (storage, resource, children) => {
	const excludedColumns = storage.get(resource);
	if (excludedColumns == null) {
		return getDefaultHiddenColumns(children);
	}
	return getFilteredExcludedColumns(excludedColumns, children);
};

export const CustomizableDatagrid = ({
	children,
	storage,
	TopComponent,
	customFieldColumns = [],
	disableColumnSettings = false,
	...props
}) => {
	const {
		resource,
		filterValues,
		exposedColumns = [],
		canMultipleChanges,
		reorderingSupport = false,
		...rest
	} = props;

    useSaveUserDataOnSuccess();
    useResetUserDataOnError();

	// If reordering is OFF, we are loaded immediately.
	const [isOrderLoaded, setIsOrderLoaded] = useState(!reorderingSupport);

	// Calculate all available columns from children + custom fields
	const allFieldColumns = useMemo(
		() => [...React.Children.toArray(children), ...customFieldColumns],
		[children, customFieldColumns]
	);

	const [excludedColumns, setExcludedColumns] = useState(
		getExcludedColumns(storage, resource, allFieldColumns)
	);

	const [orderedSources, setOrderedSources] = useState([]);

	React.useEffect(() => {
		if (storage?.isLoaded) {
			setExcludedColumns(getExcludedColumns(storage, resource, allFieldColumns));
		}
	}, [storage?.isLoaded]);

	React.useEffect(() => {
		// Skip API calls if reordering is OFF
		if (!reorderingSupport) return;

		const fetchUserOrder = async () => {
			try {
				const { data } = await dataProvider.getOne("user-data", {
					id: `columns-order-${resource}`,
				});
				const userData = data?.data ?? {};

				if (userData.columns_order) {
					setOrderedSources(userData.columns_order);
				} else {
					// No saved order found, use default code order and persist it
					const defaultOrder = allFieldColumns
						.map((col) => getColumnName(col.props))
						.filter(Boolean);
					setOrderedSources(defaultOrder);
					updateUserData(`columns-order-${resource}`, { columns_order: defaultOrder });
				}
			} catch (error) {
				// fallback to default order and persist it
				const defaultOrder = allFieldColumns.map((col) => getColumnName(col.props)).filter(Boolean);
				setOrderedSources(defaultOrder);
				updateUserData(`columns-order-${resource}`, { columns_order: defaultOrder });
			} finally {
				setIsOrderLoaded(true);
			}
		};

		fetchUserOrder();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resource, dataProvider, reorderingSupport]);

	const updateUserData = (key, data) => {
		dataProvider.update("user-data", {
			id: key,
			data: { data },
		});
	};

	const sortedFieldColumns = useMemo(() => {
		// If reordering disabled OR no user preference, use code order
		if (!reorderingSupport || orderedSources.length === 0) return allFieldColumns;

		const finalColumns = orderedSources
			.map((source) => allFieldColumns.find((col) => getColumnName(col.props) === source))
			.filter(Boolean);

		allFieldColumns.forEach((col, codeIndex) => {
			const name = getColumnName(col.props);

			if (!name || !orderedSources.includes(name)) {
				const insertIndex = Math.min(codeIndex, finalColumns.length);
				finalColumns.splice(insertIndex, 0, col);
			}
		});

		return finalColumns;
	}, [orderedSources, allFieldColumns, reorderingSupport]);

	// Handler for the drag-and-drop finish
	// The Modal returns an array of Objects { source: '...', label: '...' }
	const handleReorder = (newOrderedColumns) => {
		const newSourceList = newOrderedColumns.map((col) => col.source);
		setOrderedSources(newSourceList);

		updateUserData(`columns-order-${resource}`, {
			columns_order: newSourceList,
		});
	};

	const toggleColumn = (columnName) => {
		const newState = { ...excludedColumns };
		if (newState[columnName] === EXCLUDE) {
			delete newState[columnName];
		} else {
			newState[columnName] = EXCLUDE;
		}
		setExcludedColumns(newState);
		if (newState) {
			storage.set(resource, newState);
		}
	};

	const setAllColumnsExcluded = (excluded) => {
		const newState = excluded
			? getColumnNames(sortedFieldColumns).reduce((accumulator, columnName) => {
					accumulator[columnName] = EXCLUDE;
					return accumulator;
			  }, {})
			: {};
		setExcludedColumns(newState);
		storage.set(resource, newState);
	};

	const handleSetBulkOpen = () => props.setIsBulkOpen(!props.isBulkOpen);

	const renderChild = (child) => {
		if (child) {
			const name = getColumnName(child.props);
			if (name && excludedColumns[name] === EXCLUDE) {
				return null;
			}
			return React.cloneElement(child, {});
		}
		return null;
	};

	// Only block rendering if we are waiting for order data AND support is ON
	if (!storage?.isLoaded || (reorderingSupport && !isOrderLoaded)) {
		return null;
	}

	return (
		<>
			{TopComponent ? TopComponent : null}
			<div id={"aboveTable"} style={{ display: "flex", alignItems: "center" }}>
				{canMultipleChanges ? (
					<IconButton color="primary" onClick={handleSetBulkOpen} style={{ padding: "12px 16px" }}>
						{props.isBulkOpen ? <EnableBulkIcon /> : <DisableBulkIcon />}
					</IconButton>
				) : null}

				<ExcludeColumnsModal
					resource={resource}
					selection={excludedColumns}
					columns={getColumnLabels(sortedFieldColumns)}
					toggleColumn={toggleColumn}
					setAllColumnsExcluded={setAllColumnsExcluded}
					// Only pass the handler if reordering is supported
					// This disables the drag UI in the modal automatically
					onReorder={reorderingSupport ? handleReorder : undefined}
					disabled={disableColumnSettings}
				/>

				{exposedColumns && (
					<ColumnSelectionForm
						formGroupProps={{ row: true, style: { marginLeft: "10px" } }}
						columns={getColumnLabels(sortedFieldColumns)}
						selection={excludedColumns}
						onColumnClicked={toggleColumn}
						resource={resource}
						filterColumns={exposedColumns}
					/>
				)}
			</div>

			<DatagridEx {...rest}>{React.Children.map(sortedFieldColumns, renderChild)}</DatagridEx>
		</>
	);
};

CustomizableDatagrid.propTypes = {
	storage: T.shape({
		get: T.func.isRequired,
		set: T.func.isRequired,
	}),
	resource: T.string,
	reorderingSupport: T.bool,
};

CustomizableDatagrid.defaultProps = {
	storage: userDataSessionColumnsStorage,
	reorderingSupport: false,
};
